import { Component, Input, forwardRef, inject, signal } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { LocalizationService } from '../../core/localization/localization.service';
import { GrowthMarketingService } from '../../features/growth/growth-marketing.service';
import { isGrowthContentReadyForStorefrontApply } from '../../features/growth/growth-storefront-apply.util';

interface ContentAssetOption {
  id: string;
  label: string;
}

@Component({
  standalone: true,
  selector: 'ain-growth-content-asset-selector',
  imports: [FormsModule, SelectModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => GrowthContentAssetSelectorComponent),
      multi: true
    }
  ],
  template: `
    <p-select
      class="selector"
      [options]="options()"
      optionLabel="label"
      optionValue="id"
      [placeholder]="t('growth.selectors.contentAsset.placeholder')"
      [showClear]="showClear"
      [filter]="true"
      filterBy="label,id"
      [disabled]="disabled || loading()"
      [loading]="loading()"
      [ngModel]="value()"
      (ngModelChange)="onValueChange($event)"
      [appendTo]="appendTo" />
  `,
  styles: [`.selector { width: 100%; }`]
})
export class GrowthContentAssetSelectorComponent implements ControlValueAccessor {
  private readonly marketingService = inject(GrowthMarketingService);
  private readonly localization = inject(LocalizationService);
  private onChange: (value: string | null) => void = () => {};
  private onTouched: () => void = () => {};

  @Input() showClear = true;
  @Input() appendTo: 'body' | 'self' = 'body';
  /** When set, only assets with this status are listed (e.g. Approved). */
  @Input() statusFilter: string | null = null;
  /** Lists only content ready for storefront apply (Approved, Scheduled, Published). */
  @Input() storefrontReadyOnly = false;

  readonly loading = signal(false);
  readonly options = signal<ContentAssetOption[]>([]);
  readonly value = signal<string | null>(null);
  disabled = false;

  private pendingValue: string | null = null;
  private optionsLoaded = false;

  constructor() {
    this.load();
  }

  writeValue(value: string | null): void {
    this.pendingValue = value?.trim() || null;
    this.value.set(this.pendingValue);
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
  }

  onValueChange(value: string | null): void {
    this.value.set(value);
    this.onChange(value);
    this.onTouched();
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  reload(): void {
    this.load();
  }

  private load(): void {
    this.loading.set(true);
    this.marketingService.listContent(this.statusFilter ?? undefined).subscribe({
      next: rows => {
        const filtered = this.storefrontReadyOnly
          ? rows.filter(row => isGrowthContentReadyForStorefrontApply(row.status))
          : rows;
        this.options.set(filtered.map(row => ({ id: row.id, label: `${row.title} (${row.contentType})` })));
        this.optionsLoaded = true;
        if (this.pendingValue) {
          this.value.set(this.pendingValue);
        }
        this.loading.set(false);
      },
      error: () => {
        this.options.set([]);
        this.optionsLoaded = true;
        this.loading.set(false);
      }
    });
  }
}
